import React, { useEffect, useState, useCallback } from "react";
import { Table, Pagination, Group, Text, ActionIcon, Badge, Loader, Center, Checkbox, Button } from "@mantine/core";
import { taskService } from "../../services/taskService";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../store/useAuthStore";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";

const TaskerList = () => {
    const navigate = useNavigate();
    const isAdmin = useAuthStore((state) => state.isAdmin);

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedTaskIds, setSelectedTaskIds] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    const fetchTasks = useCallback(async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const data = await taskService.getTasks(page, pageSize);
            console.log("Tasks data:", data);
            if (data.tasks && data.tasks.length > 0) {
                console.log("First task structure:", data.tasks[0]);
            }
            setTasks(data.tasks || []);
            setSelectedTaskIds([]);
            setPagination((prev) => ({
                ...prev,
                current: data.page || page,
                pageSize: data.limit || pageSize,
                total: data.total || 0
            }));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    const { current, pageSize } = pagination;

    useEffect(() => {
        fetchTasks(current, pageSize);
    }, [fetchTasks, current, pageSize]);

    const handlePageChange = (page) => {
        fetchTasks(page, pagination.pageSize);
    };

    const updateTaskStatus = async (taskId) => {
        try {
            await taskService.updateTaskStatus(taskId);
            fetchTasks(pagination.current, pagination.pageSize);
        } catch (error) {
            console.error("Error updating task status:", error);
        }
    };

    const handleDeleteTasks = (ids) => {
        const isMultiple = ids.length > 1;
        modals.openConfirmModal({
            title: (
                <Group gap="xs">
                    <IconTrash size={18} color="red" />
                    <Text fw={700}>{isMultiple ? "Видалити задачі" : "Видалити задачу"}</Text>
                </Group>
            ),
            centered: true,
            children: (
                <Text size="sm">
                    {isMultiple
                        ? `Ви впевнені, що хочете видалити вибрані задачі (${ids.length})? Цю дію неможливо буде скасувати.`
                        : "Ви впевнені, що хочете видалити цю задачу? Цю дію неможливо буде скасувати."}
                </Text>
            ),
            labels: { confirm: 'Видалити', cancel: 'Скасувати' },
            confirmProps: { color: 'red', variant: 'filled' },
            onConfirm: async () => {
                try {
                    await taskService.deleteTasks(ids);
                    notifications.show({
                        title: 'Успішно',
                        message: isMultiple ? 'Задачі успішно видалено' : 'Задачу успішно видалено',
                        color: 'green'
                    });
                    fetchTasks(pagination.current, pagination.pageSize);
                } catch (error) {
                    console.error("Error deleting tasks:", error);
                    notifications.show({
                        title: 'Помилка',
                        message: 'Не вдалося видалити задачі. Спробуйте ще раз.',
                        color: 'red'
                    });
                }
            },
        });
    };

    if (loading) {
        return (
            <Center h={200}>
                <Loader />
            </Center>
        );
    }

    const rows = tasks.map((task) => (
        <Table.Tr key={task.id}>
            {isAdmin && (
                <Table.Td>
                    <Checkbox
                        checked={selectedTaskIds.includes(task.id)}
                        onChange={(event) =>
                            setSelectedTaskIds(
                                event.currentTarget.checked
                                    ? [...selectedTaskIds, task.id]
                                    : selectedTaskIds.filter((id) => id !== task.id)
                            )
                        }
                    />
                </Table.Td>
            )}
            <Table.Td>
                <Text size="sm" fw={500}>{task.id}</Text>
                {isAdmin && task.user?.email && (
                    <Text size="xs" c="dimmed">{task.user.email}</Text>
                )}
            </Table.Td>
            <Table.Td>{task.title}</Table.Td>
            <Table.Td>
                <Badge
                    color={task.status ? "green" : "blue"}
                    variant="light"
                    style={{ cursor: "pointer" }}
                    onClick={() => updateTaskStatus(task.id)}
                >
                    {task.status ? "Done" : "In Progress"}
                </Badge>
            </Table.Td>
            <Table.Td>
                <Group gap="xs">
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => navigate(`/tasker/edit/${task.id}`)}
                    >
                        <IconPencil size={16} />
                    </ActionIcon>
                    {isAdmin && (
                        <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => handleDeleteTasks([task.id])}
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    )}
                </Group>
            </Table.Td>
        </Table.Tr>
    ));

    return (
        <>
            {isAdmin && selectedTaskIds.length > 0 && (
                <Group mb="md">
                    <Button
                        color="red"
                        leftSection={<IconTrash size={16} />}
                        onClick={() => handleDeleteTasks(selectedTaskIds)}
                    >
                        Видалити вибрані ({selectedTaskIds.length})
                    </Button>
                </Group>
            )}

            <Table.ScrollContainer minWidth={500}>
                <Table verticalSpacing="sm" striped highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            {isAdmin && (
                                <Table.Th style={{ width: 40 }}>
                                    <Checkbox
                                        onChange={(event) =>
                                            setSelectedTaskIds(
                                                event.currentTarget.checked
                                                    ? tasks.map((t) => t.id)
                                                    : []
                                            )
                                        }
                                        checked={
                                            tasks.length > 0 &&
                                            selectedTaskIds.length === tasks.length
                                        }
                                        indeterminate={
                                            selectedTaskIds.length > 0 &&
                                            selectedTaskIds.length < tasks.length
                                        }
                                    />
                                </Table.Th>
                            )}
                            <Table.Th>ID</Table.Th>
                            <Table.Th>Title</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Actions</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{rows}</Table.Tbody>
                </Table>
            </Table.ScrollContainer>
            
            {pagination.total > 0 && (
                <Group justify="center" mt="md">
                    <Pagination
                        total={Math.ceil(pagination.total / pagination.pageSize)}
                        value={pagination.current}
                        onChange={handlePageChange}
                    />
                </Group>
            )}
        </>
    );
};

export default TaskerList;
